import { observer } from 'mobx-react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '@components/page-header';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import { Switch } from '@components/ui/switch';
import { useError } from '@components/hooks/useError';
import { PostAuthor, UserSelect } from '@components/user-select';
import { Api } from '@core/api';
import {
    CatalogAppAccessDto,
    CatalogAppDto,
    UpdateCatalogAppDto
} from '@core/api/MobileClient';
import { links } from '@core/router';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';
import { FeedbackCard, RatingCard } from './feedback-card';
import { ListingCard } from './listing-card';

const fullName = (access: CatalogAppAccessDto) =>
    [access.firstName, access.lastName].filter(Boolean).join(' ') || '—';

const AppStoreApp = observer(() => {
    const { app = '' } = useParams();
    const { mobileClient } = injectTSDI(Api);
    const showError = useError();
    const [catalogApp, setCatalogApp] = React.useState<CatalogAppDto>();
    const [accesses, setAccesses] = React.useState<CatalogAppAccessDto[]>([]);
    const [busy, setBusy] = React.useState(false);
    const [newUser, setNewUser] = React.useState<PostAuthor | null>(null);

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        try {
            await action();
            const [catalog, access] = await Promise.all([
                mobileClient.supportApi.catalogAppSupportControllerGetCatalogApps(),
                mobileClient.supportApi.catalogAppSupportControllerGetCatalogAppAccess(
                    app
                )
            ]);
            setCatalogApp(catalog.data.find((it) => it.app === app));
            setAccesses(access.data);
        } catch (e) {
            showError(e);
        } finally {
            setBusy(false);
        }
    };

    React.useEffect(() => {
        void run(async () => undefined);
    }, [app]);

    const setEnabled = (enabled: boolean) =>
        run(async () => {
            await mobileClient.supportApi.catalogAppSupportControllerUpdateCatalogApp(
                app,
                { enabled }
            );
            toast.success(`${app} ${enabled ? 'enabled' : 'disabled'}`);
        });

    const saveListing = (listing: UpdateCatalogAppDto) =>
        run(async () => {
            await mobileClient.supportApi.catalogAppSupportControllerUpdateCatalogApp(
                app,
                listing
            );
            toast.success(`${app} listing saved`);
        });

    const grant = (user: PostAuthor) =>
        run(async () => {
            await mobileClient.supportApi.userAppSupportControllerGrantUserAppAccess(
                Number(user.userId),
                app
            );
            toast.success(`${user.name} granted access to ${app}`);
            setNewUser(null);
        });

    const revoke = (accessUserId: number) =>
        run(async () => {
            await mobileClient.supportApi.userAppSupportControllerRevokeUserAppAccess(
                accessUserId,
                app
            );
            toast.success(`Access of user ${accessUserId} revoked`);
        });

    return (
        <div className="flex max-w-5xl flex-col gap-6">
            <PageHeader
                title={catalogApp?.name?.en || app}
                badges={
                    catalogApp &&
                    !catalogApp.listed && (
                        <Badge variant="secondary">Unlisted</Badge>
                    )
                }
                actions={
                    <Button variant="outline" asChild>
                        <Link to={links.appStore()}>Back to App Store</Link>
                    </Button>
                }
            />

            <Card>
                <CardHeader>
                    <CardTitle>Enabled for everyone</CardTitle>
                    <CardDescription>
                        When off, the app is hidden from every user except those
                        granted access below.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Switch
                        checked={Boolean(catalogApp?.enabled)}
                        disabled={busy || !catalogApp}
                        onCheckedChange={(enabled: boolean) =>
                            void setEnabled(enabled)
                        }
                    />
                </CardContent>
            </Card>

            <ListingCard
                catalogApp={catalogApp}
                busy={busy}
                onSave={saveListing}
            />

            <RatingCard rating={catalogApp?.rating} />

            <FeedbackCard app={app} />

            <Card>
                <CardHeader>
                    <CardTitle>Users with access</CardTitle>
                    <CardDescription>
                        These users see the app even while it is disabled for
                        everyone.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {accesses.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No user has access.
                        </p>
                    ) : (
                        <div className="flex flex-col divide-y divide-border">
                            {accesses.map((access) => (
                                <div
                                    key={access.userId}
                                    className="flex items-center justify-between gap-3 py-3"
                                >
                                    <Link
                                        className="text-sm hover:underline"
                                        to={links.userDetails(
                                            String(access.userId)
                                        )}
                                    >
                                        {fullName(access)}{' '}
                                        <span className="text-muted-foreground">
                                            #{access.userId}
                                        </span>
                                    </Link>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={busy}
                                        onClick={() =>
                                            void revoke(access.userId)
                                        }
                                    >
                                        Revoke
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
                        <UserSelect
                            label="Grant access to user"
                            value={newUser}
                            disabled={busy}
                            onChange={setNewUser}
                        />
                        <div>
                            <Button
                                disabled={busy || !newUser}
                                onClick={() => newUser && void grant(newUser)}
                            >
                                Grant access
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
});

export default AppStoreApp;
